import { useState, useMemo, useEffect, useRef } from "react";
import SelectionMarker from "./SelectionMarker";
import { useTranslation } from "react-i18next";
import { ref } from "valtio";
import { Color, MeshBasicMaterial, MeshMatcapMaterial, MeshStandardMaterial, Vector3 } from "three";
import Hotspot from "../ui/Hotspot";
import { Clone, Html, useCursor } from "@react-three/drei";
import useAnalyticsEventTracker from "../../hooks/useAnalyticsEventTracker";
import store, { cameraStore } from "../../appStore";
import RoundedPodest from "./RoundedPodest";
import { useChromeMaterial, useRemapMaterials } from "./Materials";
import { MountWithEbbne, useEbbne } from "@solidwhite/ebbnen";
import { UIPortal } from "@solidwhite/ui-portal";

import { animated, useSpring } from "@react-spring/three";
import SideBar from "../ui/SideBar/SideBar";
import DissolvableMesh from "./DissolvableMesh";
import HighlightableMesh from "./HighlightableMesh";
import { fadeToBlack } from "../../utils/fadeToBlack";
import { useSynchronizedState } from "@solidwhite/multiuser";

const findObjectStartsWithKey = ({ key, object }) => {
  if (object == null) {
    return null;
  }

  var obj = null;
  object.traverse((child) => {
    if (child.name.startsWith(key)) {
      obj = child;
    }
  });
  return obj;
};

const findChildStartsWithKey = ({ key, object }) => {
  if (object == null) {
    return null;
  }

  var obj = object.children.find((child) => child.name.startsWith(key));
  return obj;
};

//TODO refactor, too much rudundant code
const OverviewSection = ({
  object3d,
  idx,
  parentId,
  isProductSection = false,
  hideModel = false,
  hideWhenActive = false,
  ...props
}) => {
  const { visible, active, select, selectable, layer, ebbneId } = useEbbne();
  const [hovered, setHovered] = useSynchronizedState(`overviewsectionhover_${ebbneId}`, false);

  const [dissolved, setDissolved] = useSynchronizedState(`dissolved_${ebbneId}`, false);

  const { t } = useTranslation();
  const groupRef = useRef();
  const gaEventTracker = useAnalyticsEventTracker("Hotspots");

  useCursor(hovered);

  const object = useMemo(() => {
    return object3d.clone();
  }, []);

  const id = object.name;

  const selectioncolliderObject = useMemo(() => {
    if (!object) return null;
    return findObjectStartsWithKey({
      key: "collider",
      object: object,
    });
  }, [object]);

  const model = useMemo(() => {
    if (!object || hideModel) return null;
    return findChildStartsWithKey({
      key: "model",
      object: object,
    });
  }, [object]);

  useRemapMaterials(model);

  const hotspot = useMemo(() => {
    if (!object) return null;
    return findObjectStartsWithKey({
      key: "label",
      object: object,
    });
  }, [object]);

  const cameraTargetObject = useMemo(() => {
    if (!object) return null;
    var obj = object.children.find((c) => c.name.startsWith("CameraTarget"));

    if (!obj) return null;

    var obj = obj.clone();

    if (groupRef.current) {
      var worldPosition = groupRef.current.getWorldPosition(new Vector3());
      obj.position.add(worldPosition);
    }
    return obj;
  }, [object, groupRef.current]);

  useEffect(() => {
    if (active && cameraTargetObject) {
      cameraStore.cameraTargetSettings = ref(cameraTargetObject);
    }
  }, [active]);

  useEffect(() => {
    if (!selectable) {
      setHovered(false);
    }
  }, [selectable]);

  const handleClick = (e) => {
    e.stopPropagation();
    gaEventTracker(`${id} click`);
    setDissolved(true);
    fadeToBlack(() => {
      select();
    });
  };

  return (
    <group ref={groupRef} {...props} position={object.position} visible={visible}>
      {selectioncolliderObject && !dissolved && visible && (
        <SelectionMarker
          position={[selectioncolliderObject.position.x, 0.02, selectioncolliderObject.position.z]}
          selected={hovered}
          lineWidth={0.04}
          // cornerLineWidth={0.12 * 0.5}
          // cornerLength={1 * 0.5}
          lineMargin={0.15}
          visible={visible && isProductSection}
        >
          <group
            visible={false}
            onClick={selectable ? handleClick : () => {}}
            onPointerOver={(e) => {
              if (selectable) {
                e.stopPropagation();
                setHovered(true);
              }
            }}
            onPointerOut={() => setHovered(false)}
          >
            <Clone
              object={selectioncolliderObject}
              layers={[1]}
              position={[0, selectioncolliderObject.position.y, 0]}
              ref={(r) => {
                if (r) r.children.forEach((c) => c.layers.set(1));
              }}
            />
          </group>
        </SelectionMarker>
      )}
      {/* {model && visible && !(active && hideWhenActive) && ( */}
      {model && (
        <HighlightableMesh highlighted={dissolved} lineOffsetPosition={object.position} name={`Highlight_${ebbneId}`}>
          <RoundedPodest
            position={selectioncolliderObject.position}
            lenght={selectioncolliderObject.scale.x}
            width={selectioncolliderObject.scale.z}
            height={selectioncolliderObject.scale.y}
            glow={hovered}
          />
          <group>
            <primitive object={model} />
          </group>
        </HighlightableMesh>

        // <group visible={!dissolved}>
        //   <RoundedPodest
        //     position={selectioncolliderObject.position}
        //     lenght={selectioncolliderObject.scale.x}
        //     width={selectioncolliderObject.scale.z}
        //     height={selectioncolliderObject.scale.y}
        //     glow={hovered}
        //   />
        //   <group>
        //     <primitive object={model} />
        //   </group>
        // </group>
      )}

      {/* )} */}
      {visible && selectable && (
        <group>
          <Hotspot
            position={hotspot.position}
            text={hotspot.userData.textId ? t(hotspot.userData.textId) : "No ID"}
            topLeft
            onClick={selectable ? handleClick : () => {}}
            onPointerOver={() => {
              if (selectable) {
                setHovered(true);
              }
            }}
            onPointerOut={() => setHovered(false)}
            delay={300 * idx}
            id={id}
          />
        </group>
      )}

      <MountWithEbbne>
        <UIPortal>
          <SideBar key={`Sidebar_${ebbneId}`} contentId={ebbneId} />
        </UIPortal>
      </MountWithEbbne>

      <MountWithEbbne mountedWhen="active-and-below">
        <SetInActive setFunction={setDissolved} />
      </MountWithEbbne>
    </group>
  );
};

const SetInActive = ({ setFunction = (bool) => {} }) => {
  useEffect(() => {
    setFunction(true);
    return () => {
      setFunction(false);
    };
  }, []);
  return null;
};

export default OverviewSection;
